import type {
  Tkv2CommentaryAttachmentBody,
  Tkv2MarketConditionsAttachmentBody,
  Tkv2Property,
} from '@aloft/toolkit-shared';
import { TKV2_ORDER_ATTACHMENT_API } from '@aloft/toolkit-shared';

import { useCallback, useMemo } from 'react';

import { usePostTkv2CompsAPI } from '../../hooks/api/schema-hooks/usePostTkv2CompsAPI.ts';
import {
  useGetTkv2CommentaryAttachmentAPI,
  usePostTkv2XmlAttachmentAPI,
  usePutCompSequenceAPI,
  useUpdateTkv2MarketConditionsAPI,
} from '../../hooks/api/toolkitComponents.ts';
import { useToolkitContext } from '../../hooks/api/toolkitContext.ts';
import { uploadBlob } from '../../hooks/api/uploadBlob.ts';
import type { Tkv2MarketConditionsState } from '../../modules/marketConditions/types.ts';
import type { Tkv2OrderContext } from '../types.ts';
import { useNewTkv2PairedCostContext } from './useNewTkv2PairedCostContext.ts';
import { usePutTkv2Commentaries } from './usePutTkv2Commentaries.ts';
import { useTkv2Comps } from './useTkv2Comps.ts';
import { useTkv2MarketConditions } from './useTkv2MarketConditions.ts';
import { useTkv2Order } from './useTkv2Order.ts';
import { useTkv2Subject } from './useTkv2Subject.ts';

export const useNewTkv2OrderContext = (orderID: string): Tkv2OrderContext | null => {
  const tkContext = useToolkitContext();
  const params = useMemo(() => ({ pathParams: { orderID } }), [orderID]);
  // Access: Latest Order Attachments
  const { data: order } = useTkv2Order(params, { meta: { showGlobalLoader: true } });
  const [subject, setSubject, updateSubject, isSubjectDirty] = useTkv2Subject(params);
  const [comparables, setComp, isCompsDirty] = useTkv2Comps(params);
  const { refetch: refetchComps, data: compData } = comparables;
  const { refetch: refetchMarketConditions, data: marketConditions } =
    useTkv2MarketConditions(params);
  const { refetch: refetchCommentaries, data: commentaries } =
    useGetTkv2CommentaryAttachmentAPI(params);

  const { data: subjectData } = subject;

  // Mutators: useMutation returns a new object on every call
  // (https://github.com/TanStack/query/issues/1858), so destructure just what
  // you need (mutate is referentially stable).
  const { mutate: postCompMutate } = usePostTkv2CompsAPI();
  const { mutate: postXmlMutate } = usePostTkv2XmlAttachmentAPI();
  const { mutate: putCommentaryMutate } = usePutTkv2Commentaries(orderID);
  const { mutate: postMarketConditionsMutate } = useUpdateTkv2MarketConditionsAPI();
  const { mutate: putCompSequenceMutate } = usePutCompSequenceAPI();

  const compSequence = useMemo(() => compData?.sequence ?? [], [compData?.sequence]);

  const pairedCostFields = useNewTkv2PairedCostContext({
    orderID,
    subject: subjectData?.body,
    comps: compData?.comps,
    compsMC: marketConditions?.body?.comps,
    compSeq: compSequence,
    effectiveDate: order?.input?.effectiveDate,
  });

  const createComps = useCallback(
    (comps: Tkv2Property[]) => {
      postCompMutate(
        {
          ...params,
          body: TKV2_ORDER_ATTACHMENT_API.COMPS.POST.REQUEST.BODY.parse({
            comps: comps.map((c) => c.toJSON()),
          }),
        },
        {
          onSuccess: () => refetchComps(),
        },
      );
    },
    [refetchComps, params, postCompMutate],
  );

  const updateCommentaries = useCallback(
    (commentary: Tkv2CommentaryAttachmentBody) => {
      putCommentaryMutate(
        {
          ...params,
          body: TKV2_ORDER_ATTACHMENT_API.COMMENTARY.PUT.REQUEST.BODY.parse(commentary),
        },
        {
          onSettled: refetchCommentaries,
        },
      );
    },
    [refetchCommentaries, params, putCommentaryMutate],
  );

  const createXmlAttachment = useCallback(
    async (data: File, fileName: string) => {
      const res = await uploadBlob(data, fileName, tkContext);
      postXmlMutate({ ...params, body: { id: res.id } });
    },
    [postXmlMutate, params, tkContext],
  );

  const updateCompSequence = useCallback(
    (newCompSequence: string[]) => {
      putCompSequenceMutate(
        {
          ...params,
          body: TKV2_ORDER_ATTACHMENT_API.COMP_SEQUENCE.PUT.REQUEST.BODY.parse({
            sequence: newCompSequence,
          }),
        },
        {
          onSettled: () => refetchComps(),
        },
      );
    },
    [putCompSequenceMutate, params, refetchComps],
  );

  // update market conditions
  const updateMarketConditions = useCallback(
    (state: Tkv2MarketConditionsState) => {
      const comps: Tkv2MarketConditionsAttachmentBody['comps'] = {};
      Object.entries(state.comps).forEach(([compID, property]) => {
        comps[compID] = {
          termsOfSale: property.grid.termsOfSale.adjustment
            ? {
                source: 'customPrice',
                adjustmentAmount: property.grid.termsOfSale.adjustment,
              }
            : undefined,
          concessions: property.grid.concessions.adjustment
            ? {
                source: 'customPrice',
                adjustmentAmount: property.grid.concessions.adjustment,
              }
            : undefined,
          dateOfSale: property.grid.dateOfSale.adjustment
            ? {
                source: 'customPrice',
                adjustmentAmount: property.grid.dateOfSale.adjustment,
              }
            : undefined,
        };
      });

      const marketConditionsAttachmentBody: Tkv2MarketConditionsAttachmentBody = {
        version: 1,
        comps,
        cachedState: {
          dateOfSaleAdjustment: state.dateOfSaleAdjustment,
          listToSaleAdjustment: state.listToSaleAdjustment,
        },
      };

      postMarketConditionsMutate(
        {
          ...params,
          body: TKV2_ORDER_ATTACHMENT_API.MARKET_CONDITIONS.PUT.REQUEST.BODY.parse(
            marketConditionsAttachmentBody,
          ),
        },
        { onSuccess: refetchMarketConditions },
      );
    },
    [params, refetchMarketConditions, postMarketConditionsMutate],
  );

  return !order || !subjectData || !compData || !marketConditions || !pairedCostFields
    ? null
    : {
        orderID,

        order,
        createComps,
        createXmlAttachment,

        subject: subjectData,
        setSubject,
        updateSubject,
        isSubjectDirty,

        comps: compData.comps ?? {},
        compSequence,
        updateCompSequence,
        setComp,
        isCompsDirty,

        depreciationSettings: pairedCostFields?.depreciationSettings,
        saveDepreciationSettings: pairedCostFields?.saveDepreciationSettings,
        pairedCostResults: pairedCostFields?.pairedCostResults,
        pairedCostSettings: pairedCostFields?.pairedCostSettings,
        isPairedCostSettingsLoading: pairedCostFields?.isPairedCostSettingsLoading,
        savePairedCostSettings: pairedCostFields?.savePairedCostSettings,
        pairedCostVersion: pairedCostFields?.pairedCostVersion,
        setPairedCostVersion: pairedCostFields?.setPairedCostVersion,

        marketConditions: marketConditions.body,
        updateMarketConditions,

        commentaries: commentaries?.body,
        updateCommentaries,
      };
};
